import { isEmpty } from '@sgme/fp';
import { DealSplitGrid } from 'components/common-form/builder/form/node/field/DealSplit/DealSplitGrid';
import { DealSplitGridSkeleton } from 'components/common-form/builder/form/node/field/DealSplit/DealSplitGridSkeleton';
import type { Custodian } from 'models/custodian';
import type { LoadingResult } from 'models/forms/AddonFormResult';
import type { DealSplitData, DealSplitRow } from 'models/forms/DealSplitData';
import { FC, useCallback, useContext } from 'react';

import { AddonMainFormContext } from '../AddonMainForm.context';
import { useExistsValidationErrorFor } from '../ValidationErrorsContext';

interface OwnProps {
  availableCustodians?: LoadingResult<readonly Custodian[]>;
  dealSplit?: readonly DealSplitData[];
  notionalToSplit?: number;
  currency?: string;
  isReadonly?: boolean;
  onChange: (newValues: readonly DealSplitData[]) => void;
}

export const DealSplit: FC<OwnProps> = ({
  availableCustodians,
  dealSplit,
  notionalToSplit,
  currency,
  isReadonly,
  onChange,
}) => {
  const { isLoading } = useContext(AddonMainFormContext);
  const hasError = useExistsValidationErrorFor('dealSplit');

  const onRowCustodianChanged = useCallback(
    (rowIndex: number, custodianId: string) => {
      const copy = [...dealSplit!];
      copy[rowIndex].Custodian = custodianId;
      onChange(copy);
    },
    [dealSplit, onChange],
  );

  const onRowNotionalChanged = useCallback(
    (rowIndex: number, notional?: number) => {
      const copy = [...dealSplit!];
      copy[rowIndex].NotionalAmountInGrid = notional;
      onChange(copy);
    },
    [dealSplit, onChange],
  );

  const onRowDeleted = useCallback(
    (rowIndex: number) => dealSplit && onChange([...dealSplit.slice(0, rowIndex), ...dealSplit.slice(rowIndex + 1)]),
    [dealSplit, onChange],
  );

  const filterRemainingAvailableCustodians = useCallback(
    (actualCustodian?: string) => {
      const selectedCustodians = dealSplit?.map(x => x.Custodian);
      return availableCustodians!.data!.filter(
        custodian =>
          !selectedCustodians!.some(
            // remove custodian selected on the other line but keep the one selected for this line
            selectedCustodian => custodian.id === selectedCustodian && custodian.id !== actualCustodian,
          ),
      );
    },
    [dealSplit, availableCustodians],
  );

  const onRowAdded = useCallback(() => {
    const remainingCustodians = filterRemainingAvailableCustodians();
    const custodian = remainingCustodians.length === 1 ? remainingCustodians[0].id : undefined;
    onChange([...dealSplit!, { Custodian: custodian, NotionalAmountInGrid: undefined }]);
  }, [dealSplit, onChange, filterRemainingAvailableCustodians]);

  if (isLoading) {
    return <DealSplitGridSkeleton />;
  }

  if (availableCustodians === undefined || !availableCustodians.isSuccess || !availableCustodians!.data) {
    return null;
  }

  if (isEmpty(dealSplit)) {
    return null;
  }

  if (currency === undefined) {
    return null;
  }

  const rows: DealSplitRow[] = dealSplit.map(x => ({
    custodianId: x.Custodian,
    notional: x.NotionalAmountInGrid,
    availableCustodians: filterRemainingAvailableCustodians(x.Custodian),
  }));

  const allocatedNotional = dealSplit.reduce((acc, cur) => acc + (cur.NotionalAmountInGrid || 0), 0);

  const unallocatedNotional = (notionalToSplit || 0) - allocatedNotional;

  const canAdd = dealSplit.length < availableCustodians.data!.length;
  const canDelete = dealSplit.length > 1;

  return (
    <DealSplitGrid
      fieldId="dealSplit"
      onRowAdded={onRowAdded}
      onRowDeleted={onRowDeleted}
      onRowCustodianChanged={onRowCustodianChanged}
      onRowNotionalChanged={onRowNotionalChanged}
      currency={currency}
      key="dealSplit"
      dealSplits={rows}
      canAddRow={canAdd}
      canDeleteRow={canDelete}
      notionalToSplit={notionalToSplit || 0}
      unallocatedNotional={unallocatedNotional}
      isReadonly={isReadonly}
      hasError={hasError}
    />
  );
};

DealSplit.displayName = 'AddonDealSplit';
